'use client';

import { useAuth } from '@/backend/context';
import type { SaleInput, Tasa } from '@/shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SaleService } from '../services/sale-service';
import { APP_KEYS } from './query-keys'; // 👈 IMPORTAMOS LA FACTORY

export const useSale = {
  // ============================================
  // QUERIES
  // ============================================

  getPending: () => {
    const profile = useAuth((s) => s.profile);
    return useQuery({
      queryKey: [...APP_KEYS.sales.all, 'pending', profile?.id],
      queryFn: () => SaleService.getPending(profile!.id, profile!.account_type),
      enabled: !!profile,
    });
  },

  getById: (saleId: string | undefined) => {
    const profile = useAuth((s) => s.profile);
    const activeBusiness = useAuth((s) => s.activeBusiness);
    const ownerId = activeBusiness?.id || profile?.id;
    return useQuery({
      queryKey: APP_KEYS.sales.detail(ownerId || 'unknown', saleId!),
      queryFn: () => SaleService.getById(saleId!, ownerId),
      enabled: !!saleId,
      refetchInterval: 5000,
    });
  },

  getBusinessSales: (businessId: string | undefined) => {
    return useQuery({
      queryKey: APP_KEYS.sales.list(businessId!),
      queryFn: () => SaleService.Business.getGlobalSales(businessId!),
      enabled: !!businessId,
    });
  },

  // 🎯 EL NUEVO HOOK PARA EL DASHBOARD DEL BARBERO
  getByProfessional: (
    businessId: string,
    professionalId: string,
    limitCount?: number,
  ) => {
    return useQuery({
      // Añadimos el limit a la llave para que React Query distinga la lista corta de la larga
      queryKey: [
        ...APP_KEYS.sales.list(businessId),
        'professional',
        professionalId,
        limitCount,
      ],
      queryFn: () =>
        SaleService.getSalesByProfessional(
          businessId,
          professionalId,
          limitCount,
        ),
      enabled: !!professionalId,
    });
  },

  getByClient: (clientId: string | undefined) => {
    const profile = useAuth((s) => s.profile);
    return useQuery({
      queryKey: [...APP_KEYS.sales.all, 'client', clientId],
      queryFn: () =>
        SaleService.getByClient(profile!.id, profile!.account_type, clientId!),
      enabled: !!clientId && !!profile,
    });
  },

  getPeriodSales: (params: {
    businessId: string;
    workerId?: string;
    startDate: any;
    endDate: any;
  }) => {
    return useQuery({
      queryKey: [...APP_KEYS.sales.all, 'period', params],
      queryFn: () => SaleService.getPeriodSales(params),
      enabled: !!params.businessId,
    });
  },

  // ============================================
  // MUTATIONS
  // ============================================

  create: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (params: {
        data: SaleInput;
        creatorId: string;
        appointmentId?: string;
        rates: Tasa; // 👈 IMPORTANTE: Añadimos rates (puedes usar el tipo 'Tasa' si lo importas)
      }) => SaleService.create(params),

      onSuccess: (_, variables) => {
          variables.data.businessId || variables.data.professionalId;

        // 🎯 INVALIDACIONES MASIVAS (Magia de la Factory)
        // 1. Refresca todas las listas de ventas (Namespace global para coordinar Business/Pro)
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.sales.all,
        });

        // 2. Refresca la agenda porque cerramos una cita
        if (variables.appointmentId) {
          queryClient.invalidateQueries({
            queryKey: APP_KEYS.appointments.all,
          });
        }

        // 3. Refresca las finanzas (Balances y transacciones)
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.finance.all,
        });

        // 4. Refresca los clientes (Stats de visita y gasto)
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.all,
        });

        toast.success('Venta procesada');
      },
    });
  },
  update: () => {
    const queryClient = useQueryClient();
    const profile = useAuth((s) => s.profile);
    const activeBusiness = useAuth((s) => s.activeBusiness);
    const ownerId = activeBusiness?.id || profile?.id;

    return useMutation({
      mutationFn: async ({ id, data, ownerId: mutationOwnerId }: { id: string; data: any; ownerId?: string }) => {
        await SaleService.update(id, data, mutationOwnerId || ownerId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: APP_KEYS.sales.all });
      },
    });
  },

  delete: () => {
    const queryClient = useQueryClient();
    const profile = useAuth((s) => s.profile);
    const activeBusiness = useAuth((s) => s.activeBusiness);
    const ownerId = activeBusiness?.id || profile?.id;

    return useMutation({
      mutationFn: ({ saleId, userId, ownerId: mutationOwnerId }: { saleId: string; userId: string; ownerId?: string }) =>
        SaleService.delete(saleId, userId, mutationOwnerId || ownerId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: APP_KEYS.sales.all });
      },
    });
  },
};
